import { useState } from 'react';
import type { Metrics } from './engine';

const PALETTE = `
:root,[data-theme="dark"]{--sim-kept:#4cc38a;--sim-gpu:#6f7290;--sim-fly:#a9abc2}
[data-theme="light"]{--sim-kept:#1a8a55;--sim-gpu:#555872;--sim-fly:#a3a6ba}`;

interface Slice { key: string; label: string; v: number; color: string }

const money = (v: number) => {
  if (!Number.isFinite(v)) return '–';
  const a = Math.abs(v);
  return `${v < 0 ? '−' : ''}$${a.toFixed(a >= 100 ? 0 : a >= 1 || a === 0 ? 2 : a >= 0.1 ? 3 : 4)}`;
};
const pct = (v: number) => (Number.isFinite(v) ? `${v.toFixed(Math.abs(v) < 10 ? 1 : 0)}%`.replace('-', '−') : '–');

export function Money({ m }: { m: Metrics }) {
  const [hover, setHover] = useState<string | null>(null);
  const gpu = m.costGpu + m.costDisk;
  const cost = gpu + m.costFly;
  const profit = m.revenue - cost;
  const margin = m.revenue > 0 ? (100 * profit) / m.revenue : 0;
  const baseProfit = m.baselineRevenue - m.baseline;
  const baseMargin = m.baselineRevenue > 0 ? (100 * baseProfit) / m.baselineRevenue : 0;
  const loss = profit < 0;
  const slices: Slice[] = loss ? [
    { key: 'covered', label: 'Paid for by revenue', v: m.revenue, color: 'var(--sim-fly)' },
    { key: 'loss', label: 'Lost (spent beyond revenue)', v: -profit, color: 'var(--conf-low)' },
  ] : [
    { key: 'profit', label: 'Kept as profit', v: profit, color: 'var(--sim-kept)' },
    { key: 'gpu', label: 'Spent on GPU pods', v: gpu, color: 'var(--sim-gpu)' },
    { key: 'fly', label: 'Spent on Fly machines', v: m.costFly, color: 'var(--sim-fly)' },
  ];
  const whole = loss ? cost : m.revenue;
  const share = (v: number) => (whole > 0 ? (100 * v) / whole : 0);

  return (
    <div style={{ display: 'flex', gap: 18, alignItems: 'center', flexWrap: 'wrap', padding: 14 }}>
      <style>{PALETTE}</style>
      <Donut slices={slices.filter((s) => s.v > 0)} total={whole} hover={hover} setHover={setHover}
        center={m.revenue > 0 ? (
          <>
            <div style={{ fontSize: 22, fontWeight: 700, color: loss ? 'var(--conf-low)' : 'var(--ink-96)', fontVariantNumeric: 'tabular-nums' }}>{pct(margin)}</div>
            <div style={{ fontSize: 11, color: 'var(--ink-55)' }}>{loss ? 'lost' : 'kept as profit'}</div>
          </>
        ) : <><div style={{ fontSize: 15, fontWeight: 700, color: 'var(--conf-low)' }}>{money(profit)}</div><div style={{ fontSize: 11, color: 'var(--ink-55)' }}>no revenue yet</div></>} />
      <div style={{ flex: '1 1 210px', minWidth: 0, display: 'grid', gap: 7 }}>
        <Row label={loss ? 'Spent in all' : 'Customers paid'} v={money(loss ? cost : m.revenue)} strong />
        {slices.map((s) => (
          <Row key={s.key} label={s.label} v={money(s.key === 'loss' ? profit : s.v)} sub={pct(share(s.v))} color={s.color} fg={s.key === 'loss' ? 'var(--conf-low)' : undefined}
            dim={hover !== null && hover !== s.key} onHover={(on) => setHover(on ? s.key : null)} />
        ))}
        {loss && (
          <>
            <Row label="of it on GPU pods" v={money(gpu)} sub={pct(share(gpu))} />
            <Row label="of it on Fly machines" v={money(m.costFly)} sub={pct(share(m.costFly))} />
          </>
        )}
        <div style={{ borderTop: '1px solid var(--hair)', paddingTop: 7, fontSize: 11.5, color: 'var(--ink-62)', lineHeight: 1.5 }}>
          <div>Same customers with no GPU pool, everything on Fly: profit {money(baseProfit)} ({pct(baseMargin)} margin).</div>
          {m.costGpu + m.costDisk === 0 ? <div>No GPU pod has run yet, so both are the same.</div>
            : Math.abs(profit - baseProfit) < 0.0005 ? <div style={{ fontWeight: 600 }}>The GPU pool has just caught up: it starts behind (pods cost while they boot and fill), then pulls ahead once its slots are full.</div>
            : <div style={{ color: profit > baseProfit ? 'var(--ok)' : 'var(--conf-low)', fontWeight: 600 }}>{profit > baseProfit ? `The GPU pool is ${money(profit - baseProfit)} ahead` : `The GPU pool is ${money(baseProfit - profit)} behind so far`} ({profit > baseProfit ? '+' : '−'}{Math.abs(margin - baseMargin).toFixed(1)} margin points).</div>}
          <div style={{ color: 'var(--ink-45)' }}>Billed only while a session produces text (not while its machine boots); engines cost from their start. Engine cost only: AI fixes, translation, TTS and storage are not simulated.</div>
        </div>
      </div>
    </div>
  );
}

function Row({ label, v, sub, color, strong, dim, onHover, fg }: { label: string; v: string; sub?: string; color?: string; strong?: boolean; dim?: boolean; onHover?: (on: boolean) => void; fg?: string }) {
  return (
    <div onMouseEnter={() => onHover?.(true)} onMouseLeave={() => onHover?.(false)} style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 12.5, opacity: dim ? 0.5 : 1, transition: 'opacity .15s' }}>
      {color ? <span style={{ width: 10, height: 10, borderRadius: 3, background: color, flex: 'none' }} /> : <span style={{ width: 10, flex: 'none' }} />}
      <span style={{ flex: 1, color: 'var(--ink-70)', fontWeight: strong ? 600 : 400 }}>{label}</span>
      <span style={{ fontWeight: 600, color: fg ?? 'var(--ink-96)', fontVariantNumeric: 'tabular-nums' }}>{v}</span>
      <span style={{ width: 46, textAlign: 'right', color: 'var(--ink-55)', fontVariantNumeric: 'tabular-nums' }}>{sub ?? ''}</span>
    </div>
  );
}

const R = 64;
const W = 16;
const SIZE = 2 * R + 8;

function Donut({ slices, total, center, hover, setHover }: { slices: Slice[]; total: number; center: React.ReactNode; hover: string | null; setHover: (k: string | null) => void }) {
  const c = SIZE / 2;
  let a0 = -Math.PI / 2;
  const arcs = total > 0 ? slices.map((s) => {
    const a = (s.v / total) * Math.PI * 2;
    const arc = { s, a0, a1: a0 + a };
    a0 += a;
    return arc;
  }) : [];
  const path = (a0: number, a1: number, r: number) => {
    if (a1 - a0 >= Math.PI * 2 - 1e-6) a1 = a0 + Math.PI * 2 - 1e-4;
    const large = a1 - a0 > Math.PI ? 1 : 0;
    return `M${c + r * Math.cos(a0)},${c + r * Math.sin(a0)} A${r},${r} 0 ${large} 1 ${c + r * Math.cos(a1)},${c + r * Math.sin(a1)}`;
  };
  const hov = arcs.find((x) => x.s.key === hover);
  return (
    <div style={{ position: 'relative', width: SIZE, height: SIZE, flex: 'none' }}>
      <svg width={SIZE} height={SIZE} role="img" aria-label={slices.map((s) => `${s.label} ${s.v.toFixed(2)} dollars`).join(', ')}>
        <circle cx={c} cy={c} r={R - W / 2} fill="none" stroke="var(--fill-06)" strokeWidth={W} />
        {arcs.map(({ s, a0, a1 }) => (
          <path key={s.key} d={path(a0, a1, R - W / 2)} fill="none" stroke={s.color} strokeWidth={hover === s.key ? W + 4 : W}
            style={{ transition: 'stroke-width .15s', cursor: 'default' }} onMouseEnter={() => setHover(s.key)} onMouseLeave={() => setHover(null)} />
        ))}
        {arcs.length > 1 && arcs.map(({ s, a0 }) => <line key={`g${s.key}`} x1={c + (R - W - 3) * Math.cos(a0)} y1={c + (R - W - 3) * Math.sin(a0)} x2={c + (R + 3) * Math.cos(a0)} y2={c + (R + 3) * Math.sin(a0)} stroke="var(--vs-surface)" strokeWidth={2} />)}
      </svg>
      <div style={{ position: 'absolute', inset: 0, display: 'grid', placeItems: 'center', textAlign: 'center', pointerEvents: 'none' }}>
        <div>{hov ? (<><div style={{ fontSize: 15, fontWeight: 700, color: 'var(--ink-96)' }}>{money(hov.s.v)}</div><div style={{ fontSize: 11, color: 'var(--ink-55)' }}>{hov.s.label} · {pct((100 * hov.s.v) / total)}</div></>) : center}</div>
      </div>
    </div>
  );
}
